// Form fields: label above, optional hint, error message, then the control.
// The error and hint are linked with aria-describedby. Required fields are
// marked "(required)" (never with a star). `required` sets aria-required, not the
// HTML attribute, so the browser does not show its own popups: our error
// summary does that job.
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";

export type FieldCommon = Common;

type Common = {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  /** Marked "(required)"; a string is the marker in another language ("obligatoire"), for the guest pages. */
  required?: boolean | string;
  wide?: boolean;
};

function describedBy(id: string, hint?: ReactNode, error?: string): string | undefined {
  const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean);
  return ids.length ? ids.join(" ") : undefined;
}

/** Label, hint, the control, then the error (STYLE 8). Wrap any custom control with it. */
export function FieldShell({ id, label, hint, error, required, wide, children }: Common & { children: ReactNode }) {
  return (
    <div className={["field", wide ? "field--wide" : "", error ? "field--error" : ""].filter(Boolean).join(" ")}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required ? <span className="field__required"> ({typeof required === "string" ? required : "required"})</span> : null}
      </label>
      {hint ? (
        <span className="field__hint" id={`${id}-hint`}>
          {hint}
        </span>
      ) : null}
      {children}
      {error ? (
        <span className="field__error" id={`${id}-error`}>
          <span className="visually-hidden">Error: </span>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function TextField({
  id,
  label,
  hint,
  error,
  required,
  wide,
  prefix,
  ...input
}: Common & { prefix?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "prefix" | "required">) {
  const control = (
    <input
      {...input}
      id={id}
      name={input.name ?? id}
      className="input"
      // A field the page marks as doubtful (a name that looks cut off, Task 2.4) without an error message.
      aria-invalid={error ? true : input["aria-invalid"]}
      aria-required={required ? true : undefined}
      aria-describedby={describedBy(id, hint, error)}
    />
  );
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} wide={wide}>
      {prefix ? (
        <div className="input-affix">
          <span className="input-affix__text" aria-hidden="true">
            {prefix}
          </span>
          {control}
        </div>
      ) : (
        control
      )}
    </FieldShell>
  );
}

export function TextAreaField({
  id,
  label,
  hint,
  error,
  required,
  wide,
  ...textarea
}: Common & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "required">) {
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} wide={wide}>
      <textarea
        {...textarea}
        id={id}
        name={textarea.name ?? id}
        className="textarea"
        aria-invalid={error ? true : undefined}
        aria-required={required ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
      />
    </FieldShell>
  );
}

export function SelectField({
  id,
  label,
  hint,
  error,
  required,
  wide,
  options,
  ...select
}: Common & { options: { value: string; label: string; group?: string | null }[] } & Omit<SelectHTMLAttributes<HTMLSelectElement>, "id" | "required">) {
  // Options with a group are shown under it (an optgroup), in the order given.
  const blocks: { group: string | null; items: typeof options }[] = [];
  for (const o of options) {
    const g = o.group ?? null;
    const last = blocks[blocks.length - 1];
    if (last && last.group === g) last.items.push(o);
    else blocks.push({ group: g, items: [o] });
  }
  const render = (items: typeof options) =>
    items.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ));
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} required={required} wide={wide}>
      <select
        {...select}
        id={id}
        name={select.name ?? id}
        className="select"
        aria-invalid={error ? true : undefined}
        aria-required={required ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
      >
        {blocks.map((b, i) =>
          b.group ? (
            <optgroup key={`${b.group}-${i}`} label={b.group}>
              {render(b.items)}
            </optgroup>
          ) : (
            render(b.items)
          ),
        )}
      </select>
    </FieldShell>
  );
}

/** Radio buttons in a fieldset. The first radio gets `id`, so error links can jump to it. */
export function RadioGroupField<V extends string>({
  id,
  legend,
  hint,
  error,
  name,
  value,
  onChange,
  options,
}: {
  id: string;
  legend: ReactNode;
  hint?: ReactNode;
  error?: string;
  name: string;
  value: V | "";
  onChange: (value: V) => void;
  /** `hint`: a line under the option; may hold a link (the onboarding conflict card puts the source tag there). */
  options: { value: V; label: string; hint?: ReactNode }[];
}) {
  return (
    <fieldset
      className={["choice-group", error ? "field--error" : ""].filter(Boolean).join(" ")}
      aria-describedby={describedBy(id, hint, error)}
    >
      <legend>{legend}</legend>
      {hint ? (
        <span className="field__hint" id={`${id}-hint`}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={`${id}-error`}>
          <span className="visually-hidden">Error: </span>
          {error}
        </span>
      ) : null}
      {options.map((o, i) => {
        const optionId = i === 0 ? id : `${id}-${o.value}`;
        return (
          <div className="choice" key={o.value}>
            <input
              type="radio"
              id={optionId}
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              aria-describedby={o.hint ? `${optionId}-hint` : undefined}
            />
            <label htmlFor={optionId}>
              {o.label}
              {o.hint ? (
                <span className="choice__hint" id={`${optionId}-hint`}>
                  {o.hint}
                </span>
              ) : null}
            </label>
          </div>
        );
      })}
    </fieldset>
  );
}

export function CheckboxField({
  id,
  label,
  hint,
  ...input
}: { id: string; label: ReactNode; hint?: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "type">) {
  return (
    <div className="choice">
      <input {...input} type="checkbox" id={id} aria-describedby={hint ? `${id}-hint` : undefined} />
      <label htmlFor={id}>
        {label}
        {hint ? (
          <span className="choice__hint" id={`${id}-hint`}>
            {hint}
          </span>
        ) : null}
      </label>
    </div>
  );
}
